import React, { useEffect } from "react";
import PatternThumb from "./PatternThumb";
import "./PatternPicker.css";

/*
  VISCAT-386 — non-production pattern picker.

  Rendered as a direct child of .home_background, NOT inside .Home_two_buttons:
  that element carries a transform, which makes it the containing block for
  position:fixed descendants and establishes a stacking context, so a sheet nested
  inside it could neither fill the viewport nor paint above .modal.

  The grid scrolls on both axes (see PatternPicker.css). Two things work against
  in-page scrolling here: html/body.pseudo-fullscreen are pinned with
  overflow:hidden !important, and Modal installs a document-level touchmove
  preventDefault. The first only governs the page, not an inner overflow:auto
  container. The second is registered inside a DOMContentLoaded handler fired from
  a React effect, so in a bundled build that event has almost always already fired
  and the listener never attaches - but that is timing, not a guarantee, so
  touchmove is stopped here rather than relying on it.
*/
const PatternPicker = ({ patterns, gridSize = 4, onSelect, onCancel }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="pattern-picker"
      role="dialog"
      aria-modal="true"
      aria-label="Choose a pattern"
    >
      <div className="pattern-picker__sheet">
        <div className="pattern-picker__header">
          <h2 className="pattern-picker__title">Choose a pattern</h2>
          <button
            type="button"
            className="pattern-picker__close"
            onClick={onCancel}
            autoFocus
          >
            Close
          </button>
        </div>

        <div
          className="pattern-picker__grid"
          data-testid="pattern_picker_grid"
          // Keeps the scroll gesture from reaching Modal's document-level
          // touchmove preventDefault, which would otherwise cancel it.
          onTouchMove={(e) => e.stopPropagation()}
        >
          {patterns.map((pattern, i) => (
            <button
              key={i}
              type="button"
              className="pattern-picker__tile"
              // The path digits are the only identifier that crosses into the
              // backend record (patternPath), and several patterns share long
              // leading sequences, so the index alone is not enough to match a
              // reported pattern by eye.
              aria-label={`Pattern ${i}: ${pattern.path.join(", ")}`}
              onClick={() => onSelect(pattern)}
            >
              <PatternThumb pattern={pattern} size={gridSize} />
              <span className="pattern-picker__index">#{i}</span>
              <span className="pattern-picker__path">{pattern.path.join("-")}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PatternPicker;
